// Typed client for the broker connection lifecycle:
//   /api/BrokerConnections               — the caller's connections
//   /api/BrokerConnections/available     — brokers connectable in this deployment
//   /api/BrokerConnections/{p}/{env}     — store credentials / disconnect
//   .../accounts/discover, .../account   — find and pin the routing account
//   .../test, .../conformance            — prove the connection
//   .../oauth/start                      — begin a broker consent redirect
//   /api/TerminalAgent/*                 — MetaTrader terminal pairing
//
// No endpoint here ever returns a stored secret. `hasCredentials` is the only
// signal that credentials exist; a blank field on write keeps the stored value.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export type TradingEnvironment = 'Paper' | 'Live';

export interface BrokerConnectionAccount {
  accountId: string;
  label: string | null;
  type: string | null;
  currency: string | null;
}

export interface BrokerConnection {
  provider: string;
  environment: TradingEnvironment;
  authMode: string;
  status: 'Disconnected' | 'Connected' | 'NeedsReauth' | 'Error' | 'Pending';
  hasCredentials: boolean;
  accountId: string | null;
  accountLabel: string | null;
  availableAccounts: BrokerConnectionAccount[];
  liveTradingEnabled: boolean;
  lastVerifiedAt: string | null;
  lastError: string | null;
  grantedScopes: string | null;
  options: Record<string, string>;
  updatedAt: string;
}

export interface AvailableBroker {
  provider: string;
  connectorKind: 'BrokerApi' | 'BrokerNativePlatform' | 'TerminalAgent' | 'BrokerDaemon';
  authMode: 'ApiKey' | 'OAuth2' | 'OAuth1' | 'Session' | 'BrokerDaemon' | 'TerminalAgent';
  assetClasses: string[];
  environments: TradingEnvironment[];
  regions: string[];
  multipleAccounts: boolean;
  shorting: boolean;
  fractionalQuantity: boolean;
  preflight: boolean;
  streaming: boolean;
  authoritativeOrderLookup: boolean;
  oauthConfigured: boolean;
  /** False for brokers whose live switch lives with their own credentials rather than
   *  on the broker connection. The server decides which those are — the client asks
   *  instead of carrying a list that would drift. */
  liveSwitchManagedHere: boolean;
  caveats: string[];
}

export interface UpsertBrokerConnection {
  apiKey?: string;
  apiSecret?: string;
  passphrase?: string;
  username?: string;
  password?: string;
  accessToken?: string;
  accountId?: string;
  options?: Record<string, string>;
}

export interface ConnectionTest {
  success: boolean;
  message: string;
  account: {
    accountNumber: string;
    status: string;
    equity: number | null;
    cash: number | null;
    buyingPower: number | null;
  } | null;
}

export interface ConformanceCheck {
  step: number;
  name: string;
  outcome: 'Pass' | 'Fail' | 'NotSupported' | 'Skipped';
  detail: string | null;
  elapsedMs: number;
}

export interface ConformanceReport {
  provider: string;
  environment: string;
  passed: boolean;
  checks: ConformanceCheck[];
  caveats: string[];
  ranAtUtc: string;
  includedFillEvidence: boolean;
}

export interface TerminalAgentStatus {
  paired: boolean;
  live: boolean;
  accountLogin: string | null;
  brokerServer: string | null;
  brokerName: string | null;
  lastHeartbeatAt: string | null;
  snapshotAt: string | null;
  autoTradingEnabled: boolean;
  /** A terminal route has no BrokerConnection, so its live switch is reported here. */
  liveTradingEnabled: boolean;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    // Controllers answer failures as { error: "..." }; surface that, not the raw body.
    try {
      const parsed = JSON.parse(text);
      throw new Error(parsed?.error || text || `Request failed (${res.status})`);
    } catch (e: any) {
      throw new Error(e?.message || text || `Request failed (${res.status})`);
    }
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export const brokerConnectionService = {
  list: async (): Promise<BrokerConnection[]> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections`)),

  available: async (): Promise<AvailableBroker[]> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/available`)),

  upsert: async (
    provider: string,
    environment: TradingEnvironment,
    body: UpsertBrokerConnection,
  ): Promise<BrokerConnection> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })),

  disconnect: async (provider: string, environment: TradingEnvironment): Promise<void> => {
    await json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}`, {
      method: 'DELETE',
    }));
  },

  discoverAccounts: async (
    provider: string,
    environment: TradingEnvironment,
  ): Promise<BrokerConnectionAccount[]> =>
    json(await apiFetch(
      `${API_BASE}/api/BrokerConnections/${provider}/${environment}/accounts/discover`,
      { method: 'POST' },
    )),

  selectAccount: async (
    provider: string,
    environment: TradingEnvironment,
    accountId: string,
  ): Promise<BrokerConnection> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}/account`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId }),
    })),

  test: async (provider: string, environment: TradingEnvironment): Promise<ConnectionTest> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}/test`, {
      method: 'POST',
    })),

  conformance: async (
    provider: string,
    environment: TradingEnvironment,
    body: { symbol?: string; quantity?: number; referencePrice?: number; allowFillableOrder?: boolean },
  ): Promise<ConformanceReport> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}/conformance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })),

  /**
   * Terminal-agent equivalent of setLiveEnabled. A paired MetaTrader terminal has no
   * BrokerConnection document — its live switch lives on TerminalAgentSession — so the
   * two routes need separate calls even though they gate the same thing.
   */
  setTerminalLiveEnabled: async (provider: string, enabled: boolean): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/api/TerminalAgent/${encodeURIComponent(provider)}/live-enabled`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) throw new Error(await res.text() || 'Could not change the live trading switch');
  },

  setLiveEnabled: async (provider: string, enabled: boolean): Promise<void> => {
    await json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/live-enabled`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }));
  },

  startOAuth: async (
    provider: string,
    environment: TradingEnvironment,
    returnUrl: string,
  ): Promise<{ authorizeUrl: string; state: string }> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/${provider}/${environment}/oauth/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrl }),
    })),

  // E*TRADE is OAuth 1.0a with an out-of-band verifier the user copies back, so it
  // is a two-call flow rather than a redirect.
  startEtrade: async (
    environment: TradingEnvironment,
  ): Promise<{ authorizeUrl: string; requestToken: string; requestTokenSecret: string }> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/etrade/${environment}/oauth/start`, {
      method: 'POST',
    })),

  completeEtrade: async (
    environment: TradingEnvironment,
    body: { requestToken: string; requestTokenSecret: string; verifier: string },
  ): Promise<BrokerConnection> =>
    json(await apiFetch(`${API_BASE}/api/BrokerConnections/etrade/${environment}/oauth/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })),

  // ── MetaTrader terminal agent ──────────────────────────────────

  terminalStatus: async (
    provider: string,
    environment: TradingEnvironment,
  ): Promise<TerminalAgentStatus> =>
    json(await apiFetch(`${API_BASE}/api/TerminalAgent/${provider}/${environment}`)),

  pairTerminal: async (
    provider: string,
    environment: TradingEnvironment,
  ): Promise<{ code: string; expiresAt: string }> =>
    json(await apiFetch(`${API_BASE}/api/TerminalAgent/${provider}/${environment}/pair`, {
      method: 'POST',
    })),

  unpairTerminal: async (provider: string, environment: TradingEnvironment): Promise<void> => {
    await json(await apiFetch(`${API_BASE}/api/TerminalAgent/${provider}/${environment}`, {
      method: 'DELETE',
    }));
  },
};
